import PageLayout from '@/components/feature/PageLayout';
import { testimonials } from '@/mocks/data';

function AboutSchema() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '男人团',
    alternateName: '男人团男性成长社群',
    description: '男人团是专为现代男性打造的成长型社群平台，聚焦男性健康、事业、情感与自我提升。',
    url: 'https://nantuan.com',
    foundingDate: '2024',
    founder: {
      '@type': 'Person',
      name: '男人团创始团队',
    },
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于男人团',
    description: '了解男人团的品牌故事、核心价值观和联系方式。',
    url: 'https://nantuan.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '男人团',
      url: 'https://nantuan.com',
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
    </>
  );
}

export default function AboutPage() {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      const successEl = document.getElementById('form-success');
      if (successEl) successEl.style.display = 'block';
      form.reset();
      return;
    }

    const formData = new FormData(form);
    const submitAddr = 'https://readdy.ai/api/form/d9sn7c14ne17ei0sdkd0';

    fetch(submitAddr, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = null; }
        return { ok: res.ok, parsed };
      })
      .then(({ ok, parsed }) => {
        const code = parsed?.code;
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (ok && code === 'OK') {
          const successEl = document.getElementById('form-success');
          const errorEl = document.getElementById('form-error');
          if (successEl) successEl.style.display = 'block';
          if (errorEl) errorEl.style.display = 'none';
          form.reset();
        } else {
          const errorEl = document.getElementById('form-error');
          const errorMsg = document.getElementById('form-error-msg');
          if (errorEl) errorEl.style.display = 'block';
          if (errorMsg) errorMsg.textContent = serverMsg || '提交失败，请稍后再试';
        }
      })
      .catch(() => {
        const errorEl = document.getElementById('form-error');
        const errorMsg = document.getElementById('form-error-msg');
        if (errorEl) errorEl.style.display = 'block';
        if (errorMsg) errorMsg.textContent = '网络错误，请检查网络连接后重试';
      });
  };

  return (
    <PageLayout breadcrumb={[{ label: '首页', path: '/' }, { label: '关于我们' }]}>
      <AboutSchema />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground-900 mb-5">
              关于男人团
            </h1>
            <p className="text-foreground-500 text-lg max-w-2xl mx-auto leading-relaxed">
              一个专为现代男性打造的成长型社群平台
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">
                我们的故事
              </h2>
              <div className="text-foreground-700 leading-relaxed space-y-4">
                <p>
                  男人团成立于2024年，源于一个简单的念头——<strong>现代男性需要一个属于自己的成长空间</strong>。
                </p>
                <p>
                  在这个快速变化的时代，男性面临着前所未有的挑战：职场竞争日益激烈、家庭责任不断加重、健康问题频发、情感表达缺乏渠道。然而，社会对男性的期待依然是"坚强"、"不能示弱"、"独自扛下一切"。
                </p>
                <p>
                  我们相信，真正的强大不是孤军奋战，而是<strong>敢于寻求支持、乐于共同成长</strong>。男人团就是这样一个地方——在这里，你可以坦诚地面对自己的困惑，与志同道合的兄弟一起寻找答案。
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-80 md:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20masculine%20community%20gathering%20scene%2C%20group%20of%20men%20in%20meaningful%20conversation%2C%20modern%20loft%20space%2C%20golden%20hour%20lighting%2C%20authentic%20connection%2C%20brotherhood%20theme%2C%20cinematic%20warm%20tones&width=600&height=500&seq=about-story&orientation=landscape"
                alt="男人团社群"
                title="男人团 - 兄弟同行"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="mb-20">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8 text-center">
              我们的核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '真实', desc: '不装、不演、不虚假。我们鼓励每一位成员做真实的自己，分享真实的困惑与成长。' },
                { icon: 'ri-team-line', title: '互助', desc: '兄弟之间互相扶持。一个人走得快，一群人走得远。' },
                { icon: 'ri-lightbulb-line', title: '成长', desc: '持续学习和自我突破是男人团的核心精神。每一天都比昨天更好。' },
                { icon: 'ri-heart-line', title: '责任', desc: '对家庭、对事业、对社会——成为一个有担当的男人。' },
              ].map((v, i) => (
                <div key={i} className="bg-background-100 rounded-2xl p-6 text-center border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                    <i className={`${v.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              会员口碑
            </h2>
            <p className="text-foreground-500 text-base">
              听听兄弟们怎么说
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                <div className="flex items-center gap-4 mb-4">
                  <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-semibold text-foreground-900 text-sm">{t.name}</div>
                    <div className="text-foreground-400 text-xs">{t.age}岁 · {t.role}</div>
                  </div>
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed">"{t.content}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-16 md:py-20 bg-background-50">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              加入男人团
            </h2>
            <p className="text-foreground-500 text-base">
              填写以下信息，我们的社群运营团队将在24小时内与你取得联系
            </p>
          </div>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70"
          >
            <div className="mb-5">
              <label htmlFor="name" className="block text-foreground-700 text-sm font-medium mb-2">
                姓名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入你的姓名"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-2">
                邮箱 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入你的邮箱"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="interest" className="block text-foreground-700 text-sm font-medium mb-2">
                感兴趣的领域
              </label>
              <select
                id="interest"
                name="interest"
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              >
                <option value="">请选择你最关注的领域</option>
                <option value="health">男性健康</option>
                <option value="career">事业突破</option>
                <option value="relationship">情感关系</option>
                <option value="self-growth">自我提升</option>
                <option value="all">全部关注</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-foreground-700 text-sm font-medium mb-2">
                留言
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                placeholder="说说你为什么想加入男人团？（选填，500字以内）"
              ></textarea>
              <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
            </div>

            <div className="website-alt-field">
              <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            <div id="form-success" className="hidden mb-4 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm">
              提交成功！我们将在24小时内与你联系，欢迎加入男人团！
            </div>

            <div id="form-error" className="hidden mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm">
              <span id="form-error-msg">提交失败，请稍后再试</span>
            </div>

            <button
              type="submit"
              className="w-full py-3.5 rounded-xl bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              提交申请
            </button>
          </form>
        </div>
      </section>

      <section className="py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">邮箱</h3>
              <p className="text-foreground-500 text-sm">contact@nantuan.com</p>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                <i className="ri-wechat-line text-xl"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">微信公众号</h3>
              <p className="text-foreground-500 text-sm">男人团官方</p>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-3">
                <i className="ri-map-pin-line text-xl"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">地址</h3>
              <p className="text-foreground-500 text-sm">中国·杭州</p>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}